import { FC, useEffect, useMemo, useState } from 'react';
import { CourseCard } from './components/CourseCard';
import { CourseData, PricingOption } from './types';
import { COURSE_META_DATA, FORMAT_LABELS, useWebsiteCatalog } from 'entities/course-catalog';
import { admissionModel } from 'widgets/admission';
import { popoutModel } from 'shared/ui/popout-root';
import { COURSE_PROGRAMS } from 'shared/config/course-programs';
import styles from './styles.module.scss';

interface CoursesProps {
    courseIds?: string[];
    title?: string;
    format?: 'online' | 'offline';
    embedded?: boolean;
    showProgramLinks?: boolean;
}

export const Courses: FC<CoursesProps> = ({
    courseIds,
    title,
    format,
    embedded = false,
    showProgramLinks = true,
}) => {
    const { status, catalog, error, retry, refreshing } = useWebsiteCatalog();
    const [selectedFormat, setSelectedFormat] = useState<'online' | 'offline'>(format || 'online');

    useEffect(() => {
        setSelectedFormat(format || 'online');
    }, [format]);

    const visibleCourses = status === 'ready' && catalog ? catalog.courses : COURSE_META_DATA;
    const courseById = useMemo(
        () => new Map(visibleCourses.map(course => [course.id, course])),
        [visibleCourses],
    );
    const visibleProgramIds = useMemo(
        () => new Set(courseIds || COURSE_META_DATA.map(course => course.id)),
        [courseIds],
    );
    const programs = COURSE_PROGRAMS.flatMap(program => {
        const courses = program.courseIds
            .filter(courseId => visibleProgramIds.has(courseId))
            .map(courseId => courseById.get(courseId))
            .filter((course): course is CourseData => Boolean(course));
        return courses.length ? [{ ...program, courses }] : [];
    });

    const handleCourseSelect = (course: CourseData, pricing: PricingOption) => {
        admissionModel.events.setCourseSelect({
            name: course.title,
            courseData: course,
            pricingOption: pricing,
        });
        admissionModel.events.setPricingSelect(pricing);
        admissionModel.events.setTrainingSelect({ id: admissionModel.getTrainingId(pricing) });
        popoutModel.events.setPopout('admission');
    };

    return (
        <section className={`${styles.courses} ${embedded ? styles.embedded : ''}`} id="courses" aria-labelledby={embedded ? undefined : 'courses-title'}>
            <div className={styles.__in}>
                <div className={styles.toolbar}>
                {!embedded && <div>
                    <h2 className={styles.title} id="courses-title">{title || 'Выберите курс'}</h2>
                    <p className={styles.subTitle}>От первых шагов до углублённого изучения Python.</p>
                </div>}
                <div className={styles.formatSelector} role="group" aria-label="Формат занятий">
                    <span>Формат обучения</span>
                    <div className={styles.formatOptions}>
                    <button
                        type="button"
                        className={selectedFormat === 'online' ? styles.formatActive : styles.formatButton}
                        aria-pressed={selectedFormat === 'online'}
                        data-testid="catalog-format-online"
                        onClick={() => setSelectedFormat('online')}
                    >
                        {FORMAT_LABELS.online}
                    </button>
                    <button
                        type="button"
                        className={selectedFormat === 'offline' ? styles.formatActive : styles.formatButton}
                        aria-pressed={selectedFormat === 'offline'}
                        data-testid="catalog-format-offline"
                        onClick={() => setSelectedFormat('offline')}
                    >
                        {FORMAT_LABELS.offline}
                    </button>
                    </div>
                </div>
                </div>

                {status === 'error' && (
                    <div className={styles.error} role="alert">
                        <span>{error || 'Не удалось загрузить цены'}</span>
                        <button type="button" onClick={retry}>Повторить</button>
                    </div>
                )}

                <div className={styles.list}>
                    {programs.map(program => (
                        <CourseCard
                            key={program.id}
                            id={program.id}
                            title={program.title}
                            description={program.description}
                            courses={program.courses}
                            format={selectedFormat}
                            programHref={showProgramLinks ? program.programHref : undefined}
                            pricingStatus={refreshing ? 'loading' : status}
                            onSelectCourse={handleCourseSelect}
                        />
                    ))}
                </div>
                <noscript>Для загрузки актуальных цен и записи через форму включите JavaScript. Узнать стоимость и записаться можно по телефону +7 995 333 4456.</noscript>
            </div>
        </section>
    );
};
